import { MapPin, Mail, Phone } from "lucide-react";

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 bg-white"
    >
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="divider-rule" />
              <span className="eyebrow">Contact</span>
            </div>
            <h2 className="h-section">
              Let&apos;s talk
              <br />
              <span className="text-brand-blue">about your project.</span>
            </h2>
            <p className="mt-6 text-brand-text/75 leading-relaxed max-w-md">
              Whether you need a single prototype or a long-term manufacturing
              partner, our team is one message away.
            </p>

            <div className="mt-10 space-y-6">
              <ContactRow
                icon={MapPin}
                label="Plant 1 · Tirupati Autocomp Industrees"
                lines={[
                  "Plot No. 296, Sector No. 10",
                  "Pune - 411 026, Maharashtra, India",
                ]}
              />
              <ContactRow
                icon={MapPin}
                label="Plant 2 · Tirupati Engineering Works"
                lines={[
                  "J-183, Bhosari",
                  "Pune - 411 026, Maharashtra, India",
                ]}
              />
              <ContactRow
                icon={Mail}
                label="Email"
                lines={[
                  "marketing@tgscglobal.com",
                  "ceo@tgscglobal.com",
                ]}
              />
              <ContactRow
                icon={Phone}
                label="Phone &amp; Website"
                lines={[
                  "+91 91461 89436",
                  "+91 91560 96735",
                  "+91 91461 89446",
                  "www.tirupatigroups.co.in",
                ]}
              />
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="aspect-[16/12] border border-gray-200 overflow-hidden">
              <iframe
                title="Tirupati Group — Pune, India"
                src="https://www.google.com/maps?q=Sector+10%2C+Bhosari%2C+Pune%2C+Maharashtra%2C+India&output=embed"
                width="100%"
                height="100%"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="grayscale-[40%]"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, label, lines }) {
  // Plant rows get a golden treatment — name + address lines pop as a featured block.
  const isPlant = /^plant\b/i.test(label.trim());
  const labelClass = isPlant
    ? "text-[12px] uppercase tracking-[0.22em] font-extrabold"
    : "text-[11px] uppercase tracking-[0.22em] text-brand-text/55 font-semibold";
  const labelStyle = isPlant ? { color: "#C8961F" } : undefined;
  const lineClass = isPlant
    ? "block font-bold"
    : "block text-brand-navy font-semibold hover:text-brand-blue underline-offset-2 hover:underline transition";
  const plainLineStyle = isPlant ? { color: "#B07A12" } : undefined;

  return (
    <div className="flex items-start gap-4">
      <div
        className={`h-11 w-11 flex items-center justify-center text-white shrink-0 ${
          isPlant ? "" : "bg-brand-navy"
        }`}
        style={isPlant ? { backgroundColor: "#C8961F" } : undefined}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className={labelClass} style={labelStyle}>
          {label}
        </div>
        {lines.map((l) => {
          const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(l.trim());
          const isWebsite = /^(https?:\/\/|www\.)/i.test(l.trim());
          const isPhone = /^\+?\d[\d\s\-·().]{6,}$/.test(l.trim());

          if (isEmail) {
            return (
              <a
                key={l}
                href={`mailto:${l.trim()}`}
                data-testid={`contact-email-${l.trim()}`}
                className="block text-brand-navy font-semibold hover:text-brand-blue underline-offset-2 hover:underline transition"
              >
                {l}
              </a>
            );
          }
          if (isWebsite) {
            const url = l.startsWith("http") ? l : `https://${l}`;
            return (
              <a
                key={l}
                href={url}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-website"
                className="block text-brand-navy font-semibold hover:text-brand-blue underline-offset-2 hover:underline transition"
              >
                {l}
              </a>
            );
          }
          if (isPhone) {
            return (
              <a
                key={l}
                href={`tel:${l.trim().replace(/\s+/g, "")}`}
                className="block text-brand-navy font-semibold hover:text-brand-blue underline-offset-2 hover:underline transition"
              >
                {l}
              </a>
            );
          }
          return (
            <div key={l} className={lineClass} style={plainLineStyle}>
              {l}
            </div>
          );
        })}
      </div>
    </div>
  );
}
